/**
 * Single source for the v2.1 recording report section labels (docx Appendix B)
 * and the fixed learner-facing copy the UI renders itself.
 *
 * The markdown parser (report-markdown.ts) matches learner_report_md headings
 * against these labels, so a rename here must be mirrored on the prompt side.
 */
export const REPORT_SECTIONS = {
  REPORT_TYPE: 'Skill Feedback Report',
  INTERACTION_SUMMARY: 'Interaction Summary',
  SKILL_DEMONSTRATION: 'Your Skill Demonstration',
  INTERACTION_STRENGTHS: 'Interaction Strengths',
  DEVELOPMENT_AREAS: 'Development Areas',
  STEP_SPECIFIC_FEEDBACK: 'Step-Specific Feedback',
  NEXT_STEPS: 'Next Steps',
  // Step-level subsections (exist only inside Step-Specific Feedback).
  WHAT_HAPPENED: 'What Happened',
  WHAT_YOU_DID_WELL: 'What You Did Well',
  WHAT_YOU_CAN_IMPROVE: 'What You Can Improve',
  // Coach Summary (coach_addendum_md) sections, prompt rule C3.
  COACH_CONCERNS: 'Concerns to Surface',
  COACH_WINS: 'Additional Wins to Encourage',
  COACH_GROWTH: 'Additional Growth to Coach',
  COACH_DEBRIEF: 'How to Approach the Debrief',
} as const;

/** Fixed taglines under the two interaction-wide card headers. */
export const CARD_TAGLINES = {
  INTERACTION_STRENGTHS:
    'This section highlights strengths you can keep using across skills and conversations. Step-specific feedback appears below.',
  DEVELOPMENT_AREAS:
    'This section highlights areas you can strengthen across skills and conversations. Step-specific feedback appears below.',
} as const;

/**
 * "Your Skill Demonstration" card copy. The three interpretive sentences are
 * keyed by the number of filled dots (1 = lower, 2 = middle, 3 = upper range),
 * which the component derives from the Low / Medium / High granularity.
 */
export const SKILL_DEMONSTRATION = {
  levelLabel: (level: string): string => `${level} Skill Use`,
  dots: (granularity: string | null | undefined): number => {
    const g = String(granularity ?? '').toLowerCase();
    return g === 'high' ? 3 : g === 'medium' ? 2 : g === 'low' ? 1 : 0;
  },
  sentence: (dots: number, levelLabel: string): string => {
    switch (dots) {
      case 1:
        return `This interaction demonstrated ${levelLabel}, with performance falling toward the lower end of this level.`;
      case 2:
        return `This interaction demonstrated ${levelLabel}, with performance falling within the middle of this level.`;
      case 3:
        return `This interaction demonstrated ${levelLabel}, with performance falling toward the upper end of this level.`;
      default:
        return '';
    }
  },
  disclaimer:
    'This result reflects what was demonstrated in this interaction. It does not represent your overall skill level.',
};
